<template>
  <cui-filter-wrapper
    class="filter-wrapper"
    :show-clear-button="showClearButton"
    @clear-filters-clicked="emit('clear')"
  >
    <div
      class="tickets-filters__content d-flex"
      :class="isVertical ? 'flex-column mt-2 ga-2' : 'align-center'"
    >
      <slot name="prepend" />

      <v-text-field
        v-if="!isVertical && searchType === 'textfield'"
        v-model="searchModel"
        density="compact"
        variant="outlined"
        filled
        rounded
        clearable
        clear-icon="$x"
        background-color="white"
        :placeholder="t('general.search')"
        prepend-inner-icon="icon-search-xs"
        hide-details
        data-testid="tickets-page-search-field"
        class="search-filter"
        :class="{ 'filter-active': searchModel }"
        @keyup.enter.prevent
        @keydown.enter.prevent
      >
      </v-text-field>

      <v-combobox
        v-if="!isVertical && searchType === 'combobox'"
        v-model="searchModel"
        density="compact"
        variant="outlined"
        filled
        rounded
        clearable
        clear-icon="icon-close-xs"
        background-color="white"
        :items="suggestions"
        :placeholder="t('general.search')"
        prepend-inner-icon="icon-search-xs"
        append-icon=""
        hide-details
        data-testid="tickets-page-search-field"
        :class="['search-filter', { 'filter-active': searchModel }]"
        @keyup.enter.prevent
        @keydown.enter.prevent
      >
      </v-combobox>

      <slot name="after-search" />

      <v-select
        v-model="widgetModel"
        :items="widgets"
        :disabled="disabled"
        :menu-props="{ maxHeight: '300' }"
        class="widget-filter filter-menu fit"
        :class="{ 'filter-active': widgetModel }"
        :placeholder="t('tickets.filters.widgets.placeholder')"
        data-testid="tickets-page-widget-type-filter"
        density="compact"
        item-value="name"
        item-title="name"
        variant="outlined"
        rounded
        hide-details
        clearable
        @click:clear="widgetModel = null"
        background-color="white"
      >
        <template #selection="{ item }">
          <span class="body2">
            {{ t(`tickets.filters.widgets.${item.title}`) }}
          </span>
        </template>
        <template #item="{ item, props }">
          <v-list-item
            v-bind="props"
            :title="t(`tickets.filters.widgets.${item.title}`)"
            :data-testid="`tickets-page-widget-type-filter-${item}-item`"
          >
          </v-list-item>
        </template>
      </v-select>

      <ticket-type-filter
        v-model="eventTriggersModel"
        :triggers="triggers"
        :disabled="disabled"
        class="trigger-filter"
        data-test-id="tickets-page-trigger-filter"
      />

      <v-select
        v-if="showSubTriggerFilters"
        v-model="subEventTriggersModel"
        :items="subTriggers"
        :disabled="disabled"
        :menu-props="{ maxHeight: '300' }"
        class="data-type-filter"
        :class="{ 'filter-active': !!subEventTriggersModel?.length }"
        :placeholder="t('ticketDetails.filters.dataType')"
        data-testid="tickets-page-dataType-filter"
        density="compact"
        variant="outlined"
        multiple
        rounded
        hide-details
        background-color="white"
        item-value="value"
        item-title="value"
      >
        <template #selection="{ index }">
          <span v-if="index === 0" class="body2">{{ t("ticketDetails.filters.dataType") }}</span>
        </template>
        <template #label>
          <span v-if="subEventTriggersModel?.length" class="filter-label">
            {{ subEventTriggersModel.length }}
          </span>
        </template>
        <template #item="{ item, props }">
          <v-list-subheader v-if="item.raw.type === 'subheader'" :title="item.raw.value" />
          <v-list-item v-else v-bind="props">
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
              </v-list-item-action>
            </template>

            <template #title>
              {{ t(`tickets.eventSubTriggers.${item.title}`) }}
            </template>
          </v-list-item>
        </template>
      </v-select>

      <slot name="before-date-range" />

      <date-range-picker
        v-model="eventTimeRangeModel"
        :disabled="disabled"
        class="range-picker"
        :class="{ 'filter-active': eventTimeRangeModel.start && eventTimeRangeModel.end }"
        :preset-dates="dateRangePresets"
        :placeholder="t('ticketDetails.filters.during')"
        :input-props="{
          'append-icon': 'icon-arrow-down-xs',
          'hide-details': true,
        }"
        next-icon="icon-chevron-right-xs"
        data-testid="tickets-page-time-range-filter"
        prev-icon="icon-chevron-left-xs"
        density="compact"
        variant="outlined"
        :teleport="teleportDatePicker"
        :min-date="minDate"
        :max-date="maxDate"
        :range="{ showLastInRange: false, maxRange: 90 }"
      />

      <slot name="append" />
    </div>
  </cui-filter-wrapper>
</template>

<script setup lang="ts" generic="T extends TicketsFiltersType">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import DateRangePicker from "@/components/DateRangePicker.vue";
import type { TicketsFilters as TicketsFiltersType } from "@/_store/filters.module";
import type { TimeRange } from "@/_store/filters.module";
import { eventTriggerFilterMap, TicketsModuleFilter, type TriggerItem } from "@/constants/tickets";
import { getDateRangePresets, type PresetDate } from "@/constants/date-range-picker.ts";
import {
  getSubTriggers,
  sortTicketTriggers,
  type SubTriggerItem,
} from "@/_helpers/ticket-helpers.ts";
import { CuiFilterWrapper } from "@coronet/cui";
import TicketTypeFilter from "@/components/filters/TicketTypeFilter.vue";

const props = withDefaults(
  defineProps<{
    modelValue: T;
    triggers?: TriggerItem[];
    subTriggers?: SubTriggerItem[];
    showSubTriggerFilters?: boolean;
    suggestions?: string[];
    showClearButton?: boolean;
    isVertical?: boolean;
    disabled?: boolean;
    searchType?: "combobox" | "textfield";
    dateRangePresets?: PresetDate[];
    minDate?: string;
    teleportDatePicker?: boolean;
  }>(),
  {
    triggers: undefined,
    subTriggers: undefined,
    showSubTriggerFilters: false,
    suggestions: () => [],
    showClearButton: true,
    isVertical: false,
    disabled: false,
    searchType: "combobox",
    dateRangePresets: () => getDateRangePresets(),
    minDate: undefined,
    teleportDatePicker: false,
  }
);

const emit = defineEmits<{
  (event: "update:modelValue", value: T): void;
  (event: "clear"): void;
}>();

const maxDate = computed(() => moment().format("YYYY-MM-DD"));

const { t } = useI18n();

const widgets = Object.values(TicketsModuleFilter);

const triggers = computed<TriggerItem[]>(() => {
  if (props.triggers) return props.triggers;
  const widget = props.modelValue?.widget;
  return widget
    ? [
        { type: "subheader", value: t(`tickets.filters.widgets.${widget}`) },
        ...sortTicketTriggers(eventTriggerFilterMap[widget]).map((v) => ({
          type: v as string,
          value: v as string,
        })),
      ]
    : Object.entries(eventTriggerFilterMap).flatMap(([w, triggers]) => [
        { type: "subheader", value: t(`tickets.filters.widgets.${w}`) },
        ...sortTicketTriggers(triggers).map((v) => ({
          type: v as string,
          value: v as string,
        })),
      ]);
});

const subTriggers = computed<SubTriggerItem[]>(() => {
  if (props.subTriggers) return props.subTriggers;
  return getSubTriggers(props.modelValue?.eventTriggers || []);
});

const updateFilters = (patch: Partial<T>) => {
  emit("update:modelValue", {
    ...props.modelValue,
    ...patch,
  });
};

const widgetModel = computed({
  get: () => props.modelValue?.widget ?? null,
  set: (value: TicketsModuleFilter | null) => {
    updateFilters({
      widget: value,
      eventTriggers: [],
      subEventTriggers: [],
    } as unknown as Partial<T>);
  },
});

const eventTriggersModel = computed({
  get: () => props.modelValue?.eventTriggers ?? [],
  set: (value: string[]) => {
    updateFilters({
      eventTriggers: value,
      subEventTriggers: [],
    } as unknown as Partial<T>);
  },
});

const subEventTriggersModel = computed({
  get: () => props.modelValue?.subEventTriggers ?? [],
  set: (value: string[]) => {
    updateFilters({ subEventTriggers: value } as Partial<T>);
  },
});

const eventTimeRangeModel = computed({
  get: () => props.modelValue?.eventTimeRange ?? { start: "", end: "" },
  set: (value: TimeRange) => {
    updateFilters({ eventTimeRange: value } as Partial<T>);
  },
});

const searchModel = computed({
  get: () => props.modelValue?.search ?? null,
  set: (value: string | null) => {
    updateFilters({ search: value } as Partial<T>);
  },
});
</script>

<style lang="scss" scoped>
:deep(.v-field__field > .v-field__input > :is(input, .v-select__selection)) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  min-width: 0;
  max-width: 100%;
}

:deep(.clear-btn) {
  z-index: 0 !important;
}

:deep(.search-field) {
  .v-text-field__slot {
    z-index: 10 !important;
  }
}

:deep(.v-date-range__input-field .v-input__slot) {
  padding: 0 8px 0 16px !important;
}

.tickets-filters__content:not(.flex-column) {
  .widget-filter {
    width: 140px;
  }

  .trigger-filter {
    width: 118px;
  }

  .data-type-filter {
    width: 135px;
  }

  .range-picker {
    width: 140px;
    min-width: 140px;
    max-width: 245px;
  }

  .search-filter {
    width: 245px;
  }
}

.tickets-filters__content.flex-column {
  .widget-filter,
  .trigger-filter,
  .data-type-filter,
  .range-picker {
    width: 140px;
    min-width: 140px;
    max-width: 245px;
  }
}
</style>
